// client/src/hooks/useContaPlan.js (VERSÃO 100% ATUALIZADA)
import { useAuth } from '../context/auth/AuthProvider';

/**
 * Hook para acessar os recursos e limites do plano da conta
 */
export default function useContaPlan() {
  const { user } = useAuth();
  const plan = user?.account?.plano || {};
  const features = plan?.features || {};
  
  /**
   * Verifica se um recurso está disponível no plano
   * @param {string} feature - Nome do recurso (ex: 'hasAgenda')
   * @returns {boolean}
   */
  const hasFeature = (feature) => {
    return features[feature] === true;
  };
  
  /**
   * Verifica o limite de uso de um recurso
   * @param {string} resource - Nome do recurso limitado (ex: 'limiteClientes')
   * @param {number} currentUsage - Quantidade atual utilizada
   * @returns {object} { isLimited: boolean, isUnlimited: boolean, remaining: number, max: number }
   */
  const checkLimit = (resource, currentUsage = 0) => {
    const limitConfig = features[resource];
    
    // ✅ CORREÇÃO: Tratamento para limites não definidos
    if (!limitConfig || limitConfig.value === undefined) {
      return { 
        isLimited: false, 
        isUnlimited: true, 
        remaining: Infinity,
        max: Infinity,
        current: currentUsage,
        percentage: 0
      };
    }
    
    // ✅ CORREÇÃO: Verificar se é objeto ou número direto
    const limitValue = typeof limitConfig === 'object' ? limitConfig.value : limitConfig;
    const isUnlimited = typeof limitConfig === 'object' ? limitConfig.isUnlimited : false;
    
    if (isUnlimited) {
      return { 
        isLimited: false, 
        isUnlimited: true, 
        remaining: Infinity,
        max: Infinity,
        current: currentUsage,
        percentage: 0
      };
    }
    
    const remaining = limitValue - currentUsage;
    const percentage = limitValue > 0 ? (currentUsage / limitValue) * 100 : 0;
    
    return {
      isLimited: remaining <= 0,
      isUnlimited: false,
      remaining: Math.max(0, remaining),
      max: limitValue,
      current: currentUsage,
      percentage: Math.min(100, percentage)
    };
  };
  
  /**
   * Verifica se o usuário pode acessar um recurso baseado no plano
   * @param {string} feature - Nome do recurso
   * @param {string} resource - Nome do recurso limitado (opcional)
   * @param {number} currentUsage - Uso atual (opcional)
   * @returns {boolean}
   */
  const canAccess = (feature, resource, currentUsage = 0) => {
    if (!hasFeature(feature)) {
      return false;
    }
    
    if (resource) {
      const limit = checkLimit(resource, currentUsage);
      return !limit.isLimited;
    }
    
    return true;
  };

  /**
   * Verifica limite de armazenamento
   * @param {number} currentStorageBytes - Armazenamento atual em bytes
   * @returns {object}
   */
  const checkStorageLimit = (currentStorageBytes = 0) => {
    const maxStorageGB = features?.maxStorageGB || 1;
    const maxStorageBytes = maxStorageGB * (1024 ** 3);
    const percentage = maxStorageBytes > 0 ? (currentStorageBytes / maxStorageBytes) * 100 : 0;
    
    return {
      maxGB: maxStorageGB,
      maxBytes: maxStorageBytes,
      currentBytes: currentStorageBytes,
      currentGB: currentStorageBytes / (1024 ** 3),
      remainingBytes: Math.max(0, maxStorageBytes - currentStorageBytes),
      remainingGB: Math.max(0, maxStorageGB - (currentStorageBytes / (1024 ** 3))),
      percentage: Math.min(100, percentage),
      isFull: currentStorageBytes >= maxStorageBytes,
      isNearLimit: percentage >= 80
    };
  };

  /**
   * Verifica limite de tamanho de arquivo
   * @param {number} fileSizeBytes - Tamanho do arquivo em bytes
   * @returns {object}
   */
  const checkFileSizeLimit = (fileSizeBytes = 0) => {
    const maxFileSizeMB = features?.maxFileSizeMB || 10;
    const maxFileSizeBytes = maxFileSizeMB * 1024 * 1024;
    const fileSizeMB = fileSizeBytes / (1024 * 1024);
    
    return {
      maxMB: maxFileSizeMB,
      maxBytes: maxFileSizeBytes,
      currentMB: fileSizeMB,
      currentBytes: fileSizeBytes,
      isValid: fileSizeBytes <= maxFileSizeBytes,
      exceedsLimit: fileSizeBytes > maxFileSizeBytes
    };
  };

  /**
   * Obtém estatísticas do plano
   * @returns {object}
   */
  const getPlanStats = () => {
    const priceInReais = plan?.price ? plan.price / 100 : 0;
    const hasTrial = plan?.trialPeriodDays > 0;
    const isFree = priceInReais === 0;
    
    // ✅ CORREÇÃO: Contar features habilitadas de forma segura
    const enabledFeatures = Object.keys(features)
      .filter(key => key.startsWith('has') && features[key] === true)
      .length;

    // ✅ CORREÇÃO: Contar limites configurados de forma segura
    const configuredLimits = Object.keys(features)
      .filter(key => key.startsWith('limite'))
      .filter(key => {
        const limit = features[key];
        const value = typeof limit === 'object' ? limit.value : limit;
        return value > 0;
      })
      .length;

    return {
      priceInReais,
      hasTrial,
      isFree,
      trialDays: plan?.trialPeriodDays || 0,
      enabledFeatures,
      configuredLimits,
      isDefaultTrial: plan?.isDefaultTrial || false,
      isActive: plan?.isActive !== false,
      totalFeatures: Object.keys(features).filter(key => key.startsWith('has')).length
    };
  };

  /**
   * Verifica se o plano tem acesso a recursos avançados
   * @returns {object}
   */
  const getAdvancedFeatures = () => {
    return {
      hasRelatoriosAvancados: hasFeature('hasRelatoriosAvancados'),
      hasIntegracaoWhatsApp: hasFeature('hasIntegracaoWhatsApp'),
      hasIntegracaoEmail: hasFeature('hasIntegracaoEmail'),
      hasMultiplasMoedas: hasFeature('hasMultiplasMoedas'),
      hasBackupAutomatico: hasFeature('hasBackupAutomatico'),
      hasAPIAccess: hasFeature('hasAPIAccess'),
      hasWhiteLabel: hasFeature('hasWhiteLabel'),
      hasSuportePrioritario: hasFeature('hasSuportePrioritario')
    };
  };

  /**
   * Obtém todos os limites do plano de forma organizada
   * @returns {object}
   */
  const getAllLimits = () => {
    const getLimitValue = (limitKey) => {
      const limit = features[limitKey];
      if (!limit) return { value: 0, isUnlimited: false };
      
      return {
        value: typeof limit === 'object' ? limit.value : limit,
        isUnlimited: typeof limit === 'object' ? limit.isUnlimited : false
      };
    };

    return {
      // Limites principais
      usuarios: getLimitValue('limiteUsuarios'),
      recursos: getLimitValue('limiteRecursos'),
      clientes: getLimitValue('limiteClientes'),
      fornecedores: getLimitValue('limiteFornecedores'),
      servicos: getLimitValue('limiteServicos'),
      itensVenda: getLimitValue('limiteItensVenda'),
      pacotes: getLimitValue('limitePacotes'),
      contratos: getLimitValue('limiteContratos'),
      tiposContrato: getLimitValue('limiteTiposContrato'),
      templates: getLimitValue('limiteTemplates'),
      transportadores: getLimitValue('limiteTransportadores'),
      unidades: getLimitValue('limiteUnidades'),
      
      // Limites de templates específicos
      templateAnamnese: getLimitValue('limiteTemplateAnamnese'),
      templateCertificado: getLimitValue('limiteTemplateCertificado'),
      templateContrato: getLimitValue('limiteTemplateContrato'),
      templateMensagem: getLimitValue('limiteTemplateMensagem'),
      templateRecibo: getLimitValue('limiteTemplateRecibo'),
      
      // Novos limites
      ofertas: getLimitValue('limiteOfertas'),
      pacotesClientes: getLimitValue('limitePacotesClientes'),
      categorias: getLimitValue('limiteCategorias'),
      metodosPagamento: getLimitValue('limiteMetodosPagamento'),
      cursos: getLimitValue('limiteCursos'),
      
      // Limites mensais
      pedidosMensais: getLimitValue('limitePedidosMensais'),
      vendasMensais: getLimitValue('limiteVendasMensais'),
      agendamentosMensais: getLimitValue('limiteAgendamentosMensais'),
      comunicacoesMensais: getLimitValue('limiteComunicacoesMensais'),
      
      // Limites de performance
      maxStorageGB: features?.maxStorageGB || 1,
      maxFileSizeMB: features?.maxFileSizeMB || 10,
      maxAgendamentosSimultaneos: features?.maxAgendamentosSimultaneos || 1,
      retentionPeriodDays: features?.retentionPeriodDays || 90
    };
  };

  /**
   * Obtém todas as features do plano de forma organizada
   * @returns {object}
   */
  const getAllFeatures = () => {
    return {
      // Features principais
      agenda: hasFeature('hasAgenda'),
      recursos: hasFeature('hasRecursos'),
      itensVenda: hasFeature('hasItensVenda'),
      pacotes: hasFeature('hasPacotes'),
      tiposContrato: hasFeature('hasTiposContrato'),
      contratos: hasFeature('hasContratos'),
      templates: hasFeature('hasTemplates'),
      templateAnamnese: hasFeature('hasTemplateAnamnese'),
      templateCertificado: hasFeature('hasTemplateCertificado'),
      templateContrato: hasFeature('hasTemplateContrato'),
      templateRecibo: hasFeature('hasTemplateRecibo'),
      transportadores: hasFeature('hasTransportadores'),
      galeria: hasFeature('hasGaleria'),
      fornecedores: hasFeature('hasFornecedores'),
      servicos: hasFeature('hasServicos'),
      
      // Novas features
      carrinho: hasFeature('hasCarrinho'),
      categorias: hasFeature('hasCategorias'),
      comunicacao: hasFeature('hasComunicacao'),
      documentosGerados: hasFeature('hasDocumentosGerados'),
      historicoComunicacao: hasFeature('hasHistoricoComunicacao'),
      metodosPagamento: hasFeature('hasMetodosPagamento'),
      ofertas: hasFeature('hasOfertas'),
      pacotesClientes: hasFeature('hasPacotesClientes'),
      paymentGateway: hasFeature('hasPaymentGateway'),
      pagamentos: hasFeature('hasPagamentos'),
      pedidos: hasFeature('hasPedidos'),
      unidades: hasFeature('hasUnidades'),
      vendas: hasFeature('hasVendas'),
      cursos: hasFeature('hasCursos'),
      
      // Features avançadas
      ...getAdvancedFeatures()
    };
  };

  // ✅ NOVO: Verifica se o plano está ativo
  const isPlanActive = plan?.isActive !== false;

  // ✅ NOVO: Obtém informações resumidas do plano
  const getPlanSummary = () => {
    const stats = getPlanStats();
    const limits = getAllLimits();
    const allFeatures = getAllFeatures();
    
    return {
      name: plan?.name || 'Plano Gratuito',
      price: plan?.price ? plan.price / 100 : 0,
      isActive: isPlanActive,
      isTrial: user?.account?.status === 'trialing',
      hasTrial: stats.hasTrial,
      trialDays: stats.trialDays,
      enabledFeatures: stats.enabledFeatures,
      totalFeatures: stats.totalFeatures,
      mainLimits: {
        usuarios: limits.usuarios,
        clientes: limits.clientes,
        servicos: limits.servicos,
        storage: limits.maxStorageGB
      },
      keyFeatures: {
        agenda: allFeatures.agenda,
        vendas: allFeatures.vendas,
        relatorios: allFeatures.hasRelatoriosAvancados,
        suporte: allFeatures.hasSuportePrioritario
      }
    };
  };

  return {
    // Dados básicos
    plan,
    features,
    
    // Funções principais
    hasFeature,
    checkLimit,
    canAccess,
    checkStorageLimit,
    checkFileSizeLimit,
    
    // Funções de utilidade
    getPlanStats,
    getAdvancedFeatures,
    getAllLimits,
    getAllFeatures,
    getPlanSummary,
    
    // Propriedades de conveniência
    isTrial: user?.account?.status === 'trialing',
    isActive: user?.account?.status === 'active',
    planName: plan?.name || 'Plano Gratuito',
    planPrice: plan?.price ? plan.price / 100 : 0,
    trialDays: plan?.trialPeriodDays || 0,
    isFree: !plan?.price || plan.price === 0,
    isPlanActive,
    
    // Status do plano
    planStatus: {
      isTrial: user?.account?.status === 'trialing',
      isActive: user?.account?.status === 'active',
      isCanceled: user?.account?.status === 'canceled',
      isPastDue: user?.account?.status === 'past_due',
      isPlanActive: isPlanActive
    }
  };
}